import { useState } from "react";
import { Alert } from "./Alert";
import { BootstrapForm } from "./BootstrapForm";
import { useAuth } from "../hooks/useAuth";
import { formatError } from "../utils/formatError";

/**
 * The bootstrap path, for a signed-in non-administrator.
 *
 * It used to live on the login screen, which was the only place a non-admin ever landed. Now that
 * such a person lands in the chat instead, the path has to be reachable from the shell — otherwise
 * a fresh deployment could never be claimed by anyone who can also use it.
 *
 * Collapsed by default and deliberately understated: on a deployment that already has an
 * administrator this always refuses, and it must not read as an invitation.
 */
export function ClaimAdminPanel(): JSX.Element {
  const { bootstrap } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const claim = async (password: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await bootstrap(password);
    } catch (caught) {
      setError(formatError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="claim-admin">
      <button type="button" className="link-button" onClick={() => setOpen(!open)}>
        {open ? "Cancel" : "Administer this deployment"}
      </button>
      {open && (
        <div className="claim-admin__body">
          <Alert variant="info">
            Only possible while this deployment has no administrator at all. If it has one, this
            will be refused.
          </Alert>
          <BootstrapForm onSubmit={claim} busy={busy} error={error} />
        </div>
      )}
    </div>
  );
}
